import { useId, useState } from 'react';
import { DownOutlined, LinkOutlined, LogoutOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';

import { Button, Dialog, DropdownMenu, Field, Input, InputPassword } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { useLienKet, useMoLienKet, useNgatLienKet } from '../../hooks/useOpenCore';
import type { LienKetOpenCore } from '../../types';

/**
 * Nút cạnh "Bắt đầu" (`OpenCoreConnectButton` của native): kết nối tài khoản OpenCore của chính người dùng để chạy
 * Workflow trên Robot của tài khoản đó, bằng quyền của nó. Native chỉ giữ JWT OpenCore trả về, không lưu mật khẩu.
 */
export function NutKetNoiOpenCore() {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const lk = useLienKet(true);
  const ngat = useNgatLienKet();
  const [mo, setMo] = useState(false);
  const du = lk.data;

  if (!du?.connected) {
    return (
      <>
        <Button variant="outline" size="sm" icon={<LinkOutlined />} loading={lk.isPending} onClick={() => setMo(true)}>
          {t('rpaChay.ketNoi')}
        </Button>
        {mo && <HopKetNoi hienTai={du} onClose={() => setMo(false)} />}
      </>
    );
  }

  const ten = du.name || du.username;
  const trangThai = du.expired
    ? t('rpaChay.hetHan')
    : du.robotOnline
      ? t('rpaChay.online')
      : du.robotOnline === false
        ? t('rpaChay.offline')
        : t('rpaChay.chuaRo');
  const dangXuat = () =>
    ngat.mutate(undefined, {
      onSuccess: () => toast.success(t('rpaChay.daDangXuat')),
      onError: (e) => toast.error(dichLoi(e)),
    });

  return (
    <>
      <DropdownMenu
        menu={{
          items: [
            {
              key: 'thong-tin',
              disabled: true,
              label: <span className="text-xs text-content-muted">{t('rpaChay.chayTren', { ten, trangThai })}</span>,
            },
            { type: 'divider' },
            { key: 'doi', icon: <SwapOutlined />, label: t('rpaChay.doiTaiKhoan'), onClick: () => setMo(true) },
            { key: 'thoat', icon: <LogoutOutlined />, danger: true, label: t('rpaChay.dangXuat'), onClick: dangXuat },
          ],
        }}
      >
        <Button
          variant="outline"
          size="sm"
          className={cn(du.expired && 'border-warning-500 text-warning-700')}
          aria-label={t('rpaChay.taiKhoanDangKetNoi', { ten })}
          title={t('rpaChay.chayTren', { ten, trangThai })}
          loading={ngat.isPending}
        >
          <span className="inline-flex max-w-44 items-center gap-1.5">
            <span
              aria-hidden
              className={cn('inline-block size-2 shrink-0 rounded-full', !du.expired && du.robotOnline ? 'bg-success-500' : 'bg-neutral-400')}
            />
            <span className="truncate">{du.expired ? t('rpaChay.ketNoiLai', { ten }) : ten}</span>
            <DownOutlined className="text-2xs" aria-hidden />
          </span>
        </Button>
      </DropdownMenu>
      {mo && <HopKetNoi hienTai={du} onClose={() => setMo(false)} />}
    </>
  );
}

/** Đăng nhập tài khoản OpenCore. Sai mật khẩu (`401`) hay bị khoá 10 phút (`429`) báo trong hộp, hộp không đóng. */
function HopKetNoi({ hienTai, onClose }: { hienTai: LienKetOpenCore | undefined; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const moLk = useMoLienKet();
  const [taiKhoan, setTaiKhoan] = useState(hienTai?.connected ? hienTai.username : '');
  const [matKhau, setMatKhau] = useState('');
  const du = taiKhoan.trim() !== '' && matKhau !== '';

  const gui = () => {
    if (!du || moLk.isPending) return;
    moLk.mutate(
      { username: taiKhoan.trim(), password: matKhau },
      {
        onSuccess: (lk) => {
          toast.success(t('rpaChay.daKetNoi', { ten: lk.connected ? lk.name || lk.username : taiKhoan.trim() }));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={440}
      destroyOnHidden
      title={t('rpaChay.hopTieuDe')}
      footer={
        <div className="space-y-3">
          {moLk.error && <LoiTrongHop>{dichLoi(moLk.error)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={moLk.isPending} disabled={!du}>
              {t('rpaChay.nutKetNoi')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        className="space-y-3 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <Field label={t('rpaChay.taiKhoan')} required>
          {(p) => (
            <Input
              {...p}
              autoFocus
              autoComplete="username"
              prefix={<UserOutlined aria-hidden />}
              value={taiKhoan}
              onChange={(e) => {
                setTaiKhoan(e.target.value);
                moLk.reset();
              }}
            />
          )}
        </Field>
        <Field label={t('rpaChay.matKhau')} required>
          {(p) => (
            <InputPassword
              {...p}
              autoComplete="current-password"
              value={matKhau}
              onChange={(e) => {
                setMatKhau(e.target.value);
                moLk.reset();
              }}
            />
          )}
        </Field>
        <p className="text-xs text-content-muted">{t('rpaChay.ghiChu')}</p>
      </form>
    </Dialog>
  );
}
